import { get, has, values } from 'lodash';
import React, { Component } from 'react';
import styled from 'styled-components';
import { ERROR_CODES } from '@upsolve/shared';
import {
  Button, IconHeart, IconKnowledge, IconPhone, Input, HR, H5, P, Panel, Small, theme,
} from '@upsolve/ui';

import submitLead from './submitLead';

const MMI_PRIVACY_AGREEMENT_URL = 'https://www.moneymanagement.org/-/media/money-management/files/wcw/electronic-communication-disclosure.ashx';

const NAME_REGEX = /\w+\s+\w+/;
const EMAIL_REGEX = /^[a-zA-Z0-9.!#$%&’*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/;

const ReferralCheckBoxes = styled.div`
  label.mmi-option-desc {
    display: flex;
    align-items: flex-start;
    margin-bottom: 0.5em;
  }
  label.mmi-option-desc > span {
    margin-left: 0.5em;
  }
  input[type=checkbox] {
    transform: scale(1.15);
  }
`;

export default class CounselorLeadSignUp extends Component {
  constructor(props) {
    super(props);
    this.state = {
      filer: {
        name: null,
        email: null,
        phoneNumber: null,
      },
      validationErrors: {
        name: null,
        email: null,
        phoneNumber: null,
      },
      sufficientIncome: false,
      sufficientCCDebt: false,
      agreedToTerms: false,
      success: false,
      error: null,
      submitted: false,
    };
  }

  updateHelper(key, value) {
    this.setState(prevState => has(prevState, key)
      ? { ...prevState, [key]: value}
      : ({ ...prevState, filer: { ...prevState.filer, [key]: value } })
    );
  }

  validateName = (name) => NAME_REGEX.test(name) ? null : 'Please enter your first and last name';

  validateEmail = (email) => EMAIL_REGEX.test(email) ? null : 'Please enter a valid email';

  validatePhoneNumber = (phoneNumber) => phoneNumber.replace(/[^\d]/g, '').length >= 10
    ? null
    : 'Please enter a valid phone number';

  validate = (field) => this.setState({
    validationErrors: {
      ...this.state.validationErrors,
      [field]: get({
        name: this.validateName,
        email: this.validateEmail,
        phoneNumber: this.validatePhoneNumber,
      }, field, () => null)(this.state.filer[field])
    }
  });

  canSubmit = () => (
    values(this.state.filer).every((value) => !!value)
    && values(this.state.validationErrors).every((value) => value == null)
    && !(this.state.success || this.state.submitted)
    && this.state.sufficientIncome
    && this.state.sufficientCCDebt
    && this.state.agreedToTerms
  );

  async submitHelper() {
    // Don't run if we've already submitted
    if (this.state.success === true) return;
    // Otherwise try it out
    const { filer } = this.state;
    this.setState(prevState => ({ ...prevState, submitted: true }));
    try {
      // Simple capture
      await submitLead({
        type: 'debtManagementPlan',
        name: filer.name,
        email: filer.email,
        phoneNumber: filer.phoneNumber,
      });
      this.setState(prevState => ({ ...prevState, error: null, success: true, submitted: false }));
    } catch (e) {
      this.setState(prevState => ({
        ...prevState,
        error: get(e.response, 'data.error') || { message: 'An error was hit, please try submitting again.' },
        submitted: false,
      }));
    }
  }

  render() {
    const { error } = this.state;
    return (
      <Panel id="cta-next-steps">
        <div>
          <div className="panel">

            <div className="panel__header">
              <div className="message lead center">
                <H5 color={theme.colors.brand[500]}>Talk to a Free Counselor at a Nonprofit We Trust</H5>
              </div>
            </div>

            <div className="panel__content">
              <div className="cta">
                <div className="cta__external">
                  <P><b>Money Management International</b> is the leading financial counseling nonprofit in the United States. Trained staff provide free counseling to people who want to discuss alternatives to bankruptcy. Sign up below to get a referral from us. </P>
                  <div className="value-props">
                    <ul>
                      <li><IconKnowledge /> Discuss alternatives to bankruptcy for dealing with your debt</li>
                      <li><IconPhone /> One-on-one live phone sessions with trained financial counselors are available 24/7</li>
                      <li><IconHeart /> Ask questions about your personal situation.</li>
                    </ul>
                  </div>
                  <form
                    className="cta__external__form"
                    onSubmit={(e) => {
                      e.preventDefault();
                      this.submitHelper();
                    }}
                  >
                    <label>
                      <b>First and Last Name</b>
                      <Input
                        type="text"
                        disabled={this.state.success === true}
                        onBlur={() => this.validate('name')}
                        onChange={e => this.updateHelper('name', e.target.value)}
                      />
                      {this.state.validationErrors.name != null && <div><Small color="red">{this.state.validationErrors.name}</Small></div>}
                    </label>
                    <label>
                      <b>Email</b>
                      <Input
                        type="text"
                        disabled={this.state.success === true}
                        onBlur={() => this.validate('email')}
                        onChange={e => this.updateHelper('email', e.target.value)}
                      />
                      {this.state.validationErrors.email != null && <div><Small color="red">{this.state.validationErrors.email}</Small></div>}
                    </label>
                    <label>
                      <b>Phone Number</b>
                      <Input
                        type="text"
                        disabled={this.state.success === true}
                        onBlur={() => this.validate('phoneNumber')}
                        onChange={e => this.updateHelper('phoneNumber', e.target.value)}
                      />
                      {this.state.validationErrors.phoneNumber != null && <div><Small color="red">{this.state.validationErrors.phoneNumber}</Small></div>}
                    </label>
                    <ReferralCheckBoxes>
                      <label className="mmi-option-desc">
                        <Input type="checkbox" disabled={this.state.success === true} onChange={e => this.updateHelper('sufficientIncome', e.target.checked)} />
                        <span>I earn more than $35K annually (<i>at the moment we are only able to make referrals for people who earn above $35K annual income</i>).</span>
                      </label>
                      <label className="mmi-option-desc">
                        <Input type="checkbox" disabled={this.state.success === true} onChange={e => this.updateHelper('sufficientCCDebt', e.target.checked)} />
                        <span>I have $10K to $25K in credit card debt (<i>at the moment we are only able to make referrals for people with $10K-$25K in credit card debt</i>).</span>
                      </label>
                      <label className="mmi-option-desc">
                        <Input type="checkbox" disabled={this.state.success === true} onChange={e => this.updateHelper('agreedToTerms', e.target.checked)} />
                        <span>I would like MMI to contact me so I can learn about alternatives to bankruptcy like debt management. I have read and agree to <a href={MMI_PRIVACY_AGREEMENT_URL} target="_blank">MMI's Internet Privacy & Usage Agreement</a>.</span>
                      </label>
                    </ReferralCheckBoxes>
                    <HR />
                    {error != null && <div className="cta__external__form__error">
                      {error.code === ERROR_CODES.INVALID_EMAIL && <P>Please enter a valid email so we can send you information to help.</P>}
                      {error.code === ERROR_CODES.INVALID_PHONE_NUMBER && <P>Please enter a valid US phone number so we can get you in touch with help.</P>}
                      {error.code === ERROR_CODES.FILER_EXISTS && <P>It looks like we already have your information on file!</P>}
                      {error.code == null && <P>There was an error. Please check your information and try again.</P>}
                    </div>}
                    <Button
                      type="submit"
                      size="lg"
                      width="100%"
                      disabled={!this.canSubmit()}
                    >
                      Sign up for Referral
                    </Button>
                    {this.state.success === true && (
                      <div className="cta__external__form__success">
                        <P textAlign="center">
                          <b>Great!</b> Money Management International will reach out to you.
                        </P>
                      </div>
                    )}
                  </form>
                </div>
              </div>
            </div>

          </div>
        </div>
      </Panel>
    );
  }
}
